import { css } from 'aphrodite';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { useMyCategories } from '../../../queries/Scenarios/useCategories';
import { useMyCompletions } from '../../../queries/Scenarios/useScenarioCompletions';
import { useMyScenarios } from '../../../queries/Scenarios/useScenarios';
import { useMySkillAreas } from '../../../queries/Scenarios/useSkillAreas';
import type {
	Category,
	ScenarioCompletion,
	ScenarioWithCompletion,
	SkillArea,
} from '../../../voice-agent/types/scenarios';
import { BasicPage } from '../../BasicPage';
import { CategoryProgress } from './CategoryProgress';
import { ScenarioDetailModal } from './ScenarioDetailModal';
import { styleSheet } from './styles';

interface SkillAreaWithScenarios {
	skillArea: SkillArea;
	scenarios: ScenarioWithCompletion[];
}

interface ProcessedCategory {
	category: Category;
	skillAreasWithScenarios: SkillAreaWithScenarios[];
	totalScenarios: number;
	practicedScenarios: number;
	averageScore: number | null;
}

/**
 * ScenarioProgress displays a user's training progress across all scenarios and skill areas
 * organized by category, with drill-down capability to view detailed completion history.
 *
 * Hierarchy: Category → SkillArea → Scenario
 */
export const ScenarioProgress: React.FC = () => {
	const [selectedScenario, setSelectedScenario] = useState<{
		scenario: ScenarioWithCompletion;
		completions: ScenarioCompletion[];
	} | null>(null);

	// Fetch data - all using my- endpoints for role-based filtering (defaults to SDR if no role set)
	const { data: scenarios, isLoading: scenariosLoading, error: scenariosError } = useMyScenarios({ enabled: true });
	const {
		data: completions,
		isLoading: completionsLoading,
		error: completionsError,
	} = useMyCompletions({ enabled: true });
	const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useMyCategories({ enabled: true });
	const { data: skillAreas, isLoading: skillAreasLoading, error: skillAreasError } = useMySkillAreas();

	const isLoading = scenariosLoading || completionsLoading || categoriesLoading || skillAreasLoading;
	const error = scenariosError || completionsError || categoriesError || skillAreasError;

	// Hooks now return arrays directly
	const rawScenariosArray = useMemo(() => scenarios || [], [scenarios]);
	const categoriesArray = useMemo(() => categories || [], [categories]);
	const skillAreasArray = useMemo(() => skillAreas || [], [skillAreas]);
	const completionsArray = useMemo(() => completions || [], [completions]);

	// Merge completions data into scenarios (compute attemptCount, bestScore, lastCompletion)
	const scenariosArray = useMemo((): ScenarioWithCompletion[] => {
		if (rawScenariosArray.length === 0) return [];

		return rawScenariosArray.map(scenario => {
			// Find all completions for this scenario
			const scenarioCompletions = completionsArray.filter((c: ScenarioCompletion) => c.scenarioId === scenario.id);

			if (scenarioCompletions.length === 0) {
				return scenario;
			}

			// Compute stats from completions
			const attemptCount = scenarioCompletions.length;
			const completedCompletions = scenarioCompletions.filter(
				(c: ScenarioCompletion) => c.status === 'Completed' && c.score !== null
			);
			const scenarioBestScore =
				completedCompletions.length > 0
					? Math.max(...completedCompletions.map((c: ScenarioCompletion) => c.score as number))
					: undefined;

			// Find the most recent completion
			const sortedCompletions = [...scenarioCompletions].sort(
				(a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()
			);
			const lastCompletion = sortedCompletions[0];

			return {
				...scenario,
				attemptCount,
				bestScore: scenarioBestScore,
				lastCompletion,
			};
		});
	}, [rawScenariosArray, completionsArray]);

	// Create a set of category IDs for filtering skill areas
	const categoryIds = useMemo(() => new Set(categoriesArray.map((c: Category) => c.id)), [categoriesArray]);

	// Filter skill areas to only those belonging to user's categories
	const relevantSkillAreas = useMemo(
		() => skillAreasArray.filter((sa: SkillArea) => categoryIds.has(sa.categoryId)),
		[skillAreasArray, categoryIds]
	);

	// Process data with memoization - group by Category → SkillArea → Scenario
	const processedData = useMemo((): ProcessedCategory[] => {
		if (scenariosArray.length === 0 || categoriesArray.length === 0) return [];

		return categoriesArray
			.map((category: Category) => {
				// Get skill areas for this category
				const categorySkillAreas = relevantSkillAreas.filter((sa: SkillArea) => sa.categoryId === category.id);

				// Build skill areas with their scenarios
				const skillAreasWithScenarios: SkillAreaWithScenarios[] = categorySkillAreas
					.map((skillArea: SkillArea) => {
						const areaScenarios = scenariosArray.filter((s: ScenarioWithCompletion) => s.skillAreaId === skillArea.id);
						return {
							skillArea,
							scenarios: areaScenarios,
						};
					})
					.filter((sa: SkillAreaWithScenarios) => sa.scenarios.length > 0);

				// Calculate category-level stats
				const allCategoryScenarios = skillAreasWithScenarios.flatMap(sa => sa.scenarios);
				const practicedScenarios = allCategoryScenarios.filter(
					(s: ScenarioWithCompletion) => s.attemptCount && s.attemptCount > 0
				);
				const avgScore =
					practicedScenarios.length > 0
						? practicedScenarios
								.filter((s: ScenarioWithCompletion) => s.bestScore !== undefined && s.bestScore !== null)
								.reduce((sum: number, s: ScenarioWithCompletion) => sum + (s.bestScore || 0), 0) /
							practicedScenarios.length
						: null;

				return {
					category,
					skillAreasWithScenarios,
					totalScenarios: allCategoryScenarios.length,
					practicedScenarios: practicedScenarios.length,
					averageScore: avgScore,
				};
			})
			.filter((cat: ProcessedCategory) => cat.totalScenarios > 0);
	}, [scenariosArray, categoriesArray, relevantSkillAreas]);

	// Calculate overall stats with memoization
	const { totalAttempts, averageScore, bestScore } = useMemo(() => {
		if (scenariosArray.length === 0) {
			return { totalAttempts: 0, averageScore: 0, bestScore: 0 };
		}

		const attemptsSum = scenariosArray.reduce(
			(sum: number, s: ScenarioWithCompletion) => sum + (s.attemptCount || 0),
			0
		);
		const allScoresWithValues = scenariosArray.filter(
			(s: ScenarioWithCompletion) => s.bestScore !== undefined && s.bestScore !== null
		);
		const avgScore =
			allScoresWithValues.length > 0
				? allScoresWithValues.reduce((sum: number, s: ScenarioWithCompletion) => sum + (s.bestScore || 0), 0) /
					allScoresWithValues.length
				: 0;
		const maxScore = scenariosArray.reduce(
			(max: number, s: ScenarioWithCompletion) => Math.max(max, s.bestScore || 0),
			0
		);

		return { totalAttempts: attemptsSum, averageScore: avgScore, bestScore: maxScore };
	}, [scenariosArray]);

	const handleScenarioClick = (scenario: ScenarioWithCompletion) => {
		// Filter completions for this scenario
		const scenarioCompletions = completionsArray.filter((c: ScenarioCompletion) => c.scenarioId === scenario.id);

		setSelectedScenario({
			scenario,
			completions: scenarioCompletions,
		});
	};

	const handleCloseModal = () => {
		setSelectedScenario(null);
	};

	if (isLoading) {
		return (
			<BasicPage title='Training Progress'>
				<div className={css(styleSheet.loading)}>Loading your training progress...</div>
			</BasicPage>
		);
	}

	if (error) {
		return (
			<BasicPage title='Training Progress'>
				<div className={css(styleSheet.container)}>
					<div className={css(styleSheet.error)}>Failed to load training progress. Please try again later.</div>
				</div>
			</BasicPage>
		);
	}

	return (
		<BasicPage title='Training Progress'>
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.contentWrapper)}>
					<div className={css(styleSheet.header)}>
						<h1 className={css(styleSheet.pageTitle)}>Your Training Progress</h1>
						<div className={css(styleSheet.statsContainer)}>
							<div className={css(styleSheet.statItem)}>
								<div className={css(styleSheet.statLabel)}>Total Attempts</div>
								<div className={css(styleSheet.statValue)}>{totalAttempts}</div>
							</div>
							{averageScore > 0 && (
								<div className={css(styleSheet.statItem)}>
									<div className={css(styleSheet.statLabel)}>Average Score</div>
									<div className={css(styleSheet.statValue)}>{averageScore.toFixed(1)}</div>
								</div>
							)}
							{bestScore > 0 && (
								<div className={css(styleSheet.statItem)}>
									<div className={css(styleSheet.statLabel)}>Best Score</div>
									<div className={css(styleSheet.statValue)}>{bestScore}</div>
								</div>
							)}
						</div>
					</div>

					{processedData.length === 0 ? (
						<div className={css(styleSheet.emptyState)}>
							No training scenarios available for your role. Check back later!
						</div>
					) : (
						processedData.map(categoryData => (
							<CategoryProgress
								key={categoryData.category.id}
								category={categoryData.category}
								skillAreasWithScenarios={categoryData.skillAreasWithScenarios}
								onScenarioClick={handleScenarioClick}
							/>
						))
					)}
				</div>

				{selectedScenario && (
					<ScenarioDetailModal
						scenario={selectedScenario.scenario}
						completions={selectedScenario.completions}
						bestScore={selectedScenario.scenario.bestScore || null}
						attemptCount={selectedScenario.scenario.attemptCount || 0}
						onClose={handleCloseModal}
					/>
				)}
			</div>
		</BasicPage>
	);
};
